/**
 * Frontend mirror of apps/back/src/api/models/Secret.js#SECRET_TYPE_SCHEMAS.
 * Must stay in sync with the backend definition; backend Joi validation is
 * generated from the same shape.
 *
 * `input` is one of: 'text' | 'password' | 'textarea' | 'select' | 'kv_list'.
 * Optional per field:
 *   - `helper`: text rendered under the label
 *   - `placeholder`: placeholder attribute for the input
 *   - `options`: array of string values (only used when input === 'select')
 *   - `when`: `{ [otherFieldKey]: expectedValue }` - field is only rendered
 *      and validated when the referenced field currently equals `expectedValue`.
 *
 * ElevenLabs and LiveKit are intentionally excluded: they have provider-specific
 * onboarding flows handled elsewhere and do not fit this generic shape.
 */
const SECRET_TYPE_SCHEMAS = {
  bearer_token: {
    fields: [{ key: 'token', input: 'password', required: true }],
  },
  api_key: {
    fields: [
      { key: 'api_key', input: 'password', required: true },
      { key: 'header_name', input: 'text', required: true, placeholder: 'X-API-Key' },
    ],
  },
  basic_auth: {
    fields: [
      { key: 'username', input: 'text', required: true },
      { key: 'password', input: 'password', required: true },
    ],
  },
  certificate: {
    fields: [
      {
        key: 'certificate',
        input: 'textarea',
        required: true,
        helper: 'Paste your SSL/TLS certificate content',
        placeholder: '-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----',
      },
      {
        key: 'certificate_chain',
        input: 'textarea',
        required: false,
        helper: 'Optional: intermediate certificates in the chain',
        placeholder: '-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----',
      },
      {
        key: 'passphrase',
        input: 'password',
        required: false,
        helper: 'Optional: passphrase for encrypted private key',
        placeholder: 'Enter passphrase if certificate is encrypted',
      },
      {
        key: 'private_key',
        input: 'textarea',
        required: true,
        helper: 'Paste your private key content',
        placeholder: '-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----',
      },
    ],
  },
  oauth2: {
    fields: [
      { key: 'client_id', input: 'text', required: true },
      { key: 'client_secret', input: 'password', required: true },
      {
        key: 'authorization_url',
        input: 'text',
        required: true,
        placeholder: 'https://provider.com/oauth/authorize',
      },
      {
        key: 'token_url',
        input: 'text',
        required: true,
        placeholder: 'https://provider.com/oauth/token',
      },
      { key: 'scopes', input: 'text', required: false, placeholder: 'read write' },
      {
        key: 'redirect_uri',
        input: 'text',
        required: false,
        placeholder: 'https://yourapp.com/oauth/callback',
      },
    ],
  },
  aws_credentials: {
    fields: [
      {
        key: 'authentication_method',
        input: 'select',
        required: true,
        helper: 'Choose between Access Key or IAM Role AssumeRole',
        options: ['access_key', 'assume_role'],
        default: 'access_key',
      },
      {
        key: 'access_key_id',
        input: 'text',
        required: true,
        helper: 'AWS IAM Access Key ID',
        placeholder: 'AKIAIOSFODNN7EXAMPLE',
        when: { authentication_method: 'access_key' },
      },
      {
        key: 'secret_access_key',
        input: 'password',
        required: true,
        helper: 'AWS IAM Secret Access Key',
        placeholder: 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY',
        when: { authentication_method: 'access_key' },
      },
      {
        key: 'iam_role_arn',
        input: 'text',
        required: true,
        helper: 'AWS IAM Role ARN to assume',
        placeholder: 'arn:aws:iam::123456789012:role/MyRole',
        when: { authentication_method: 'assume_role' },
      },
      {
        key: 'external_id',
        input: 'text',
        required: false,
        helper: 'Optional External ID for secure cross-account role assumption',
        placeholder: 'unique-external-id',
        when: { authentication_method: 'assume_role' },
      },
    ],
  },
  azure_storage: {
    fields: [
      { key: 'account_name', input: 'text', required: true, placeholder: 'mystorageaccount' },
      { key: 'account_key', input: 'password', required: false },
      { key: 'connection_string', input: 'password', required: false },
      {
        key: 'client_id',
        input: 'text',
        required: false,
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      },
      { key: 'client_secret', input: 'password', required: false },
      {
        key: 'tenant_id',
        input: 'text',
        required: false,
        placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      },
    ],
  },
  gcp_service_account: {
    fields: [
      {
        key: 'service_account_json',
        input: 'textarea',
        required: true,
        placeholder: '{\n  "type": "service_account",\n  "project_id": "..."\n}',
      },
    ],
  },
  http_headers: {
    fields: [{ key: 'headers', input: 'kv_list', required: true }],
  },
  other: {
    fields: [{ key: 'entries', input: 'kv_list', required: true }],
  },
};

export const SECRET_TYPES = Object.keys(SECRET_TYPE_SCHEMAS);

export const getSecretTypeSchema = type => SECRET_TYPE_SCHEMAS[type];

export const isSecretTypeSupported = type => Boolean(SECRET_TYPE_SCHEMAS[type]);

export default SECRET_TYPE_SCHEMAS;
